import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { Session } from '@supabase/supabase-js'

/** The signed-in user as the app holds it: what `convertSupabaseUser` (core/lib/supabase.js) builds from the Supabase user. */
export interface AuthUser {
  id: string
  name: string
  email: string
  avatar_url?: string
  provider: string
}

export interface AuthState {
  isAuthenticated: boolean
  user: AuthUser | null
  session: Session | null
  accessToken: string | null
  /** Starts true to prevent a flash of unauthenticated content before the session check completes. */
  loading: boolean
  /** Whether the essential data (global configs) has been loaded. */
  appInitialized: boolean
  /** Whether the essential data is currently loading. */
  isLoadingEssentialData: boolean
  /** Whether the signed-in user is not authorized to use the application. */
  isUnauthorized: boolean
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  session: null,
  accessToken: null,
  loading: true,
  appInitialized: false,
  isLoadingEssentialData: false,
  isUnauthorized: false
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{ user: AuthUser; session: Session; accessToken: string }>) => {
      state.isAuthenticated = true
      state.user = action.payload.user
      state.session = action.payload.session
      state.accessToken = action.payload.accessToken
      state.loading = false
      // Preserve existing app initialization state - don't reset it
      // appInitialized and isLoadingEssentialData should only be modified by their specific actions
    },
    logout: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.session = null
      state.accessToken = null
      state.loading = false
      state.appInitialized = false
      state.isLoadingEssentialData = false
      state.isUnauthorized = false
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    updateSession: (state, action: PayloadAction<Session>) => {
      state.session = action.payload
      state.accessToken = action.payload.access_token
    },
    setLoadingEssentialData: (state, action: PayloadAction<boolean>) => {
      state.isLoadingEssentialData = action.payload
    },
    setAppInitialized: (state, action: PayloadAction<boolean>) => {
      state.appInitialized = action.payload
    },
    setUnauthorized: (state, action: PayloadAction<boolean>) => {
      state.isUnauthorized = action.payload
    }
  }
})

export const { login, logout, setLoading, updateSession, setLoadingEssentialData, setAppInitialized, setUnauthorized } =
  authSlice.actions

export default authSlice.reducer
